import { useEffect, useMemo, useState } from 'react'
import { Mail, Search, Star } from 'lucide-react'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import { Button } from '@/components/ui/button'
import { useReportPreferences } from '@/contexts/ReportPreferencesContext'
import { fetchReportCatalog, scheduleReportEmail } from '@/lib/api/reports'
import { REPORT_CATEGORY_LABELS } from '@/lib/reports/preferences'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'

/**
 * @param {{ sections: Array<{ key: string, name: string, category: string }> }} props
 */
export function ReportsHub({ sections }) {
  const {
    period,
    setPeriod,
    compare,
    setCompare,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    category,
    setCategory,
    search,
    setSearch,
    favouritesOnly,
    setFavouritesOnly,
    favourites,
    toggleFavourite,
    isFavourite,
    activeReportKey,
    setActiveReportKey,
  } = useReportPreferences()

  const [catalogCategories, setCatalogCategories] = useState([])
  const [scheduleStatus, setScheduleStatus] = useState('')
  const [scheduling, setScheduling] = useState(false)

  useEffect(() => {
    fetchReportCatalog()
      .then((data) => {
        const cats = [...new Set((data.reports ?? []).map((r) => r.category).filter(Boolean))]
        setCatalogCategories(cats)
      })
      .catch(() => {
        setCatalogCategories([...new Set(sections.map((s) => s.category))])
      })
  }, [sections])

  const categories = useMemo(() => {
    const fromSections = [...new Set(sections.map((s) => s.category))]
    const merged = [...new Set([...catalogCategories, ...fromSections])]
    return merged.sort()
  }, [catalogCategories, sections])

  const visibleSections = useMemo(
    () =>
      sections.filter((section) => {
        if (favouritesOnly && !favourites.includes(section.key)) return false
        if (category !== 'all' && section.category !== category) return false
        if (search.trim()) {
          const q = search.trim().toLowerCase()
          const haystack = `${section.name} ${section.key} ${section.category}`.toLowerCase()
          if (!haystack.includes(q)) return false
        }
        return true
      }),
    [sections, favouritesOnly, favourites, category, search],
  )

  useEffect(() => {
    if (visibleSections.length === 0) {
      return
    }

    if (!visibleSections.some((section) => section.key === activeReportKey)) {
      setActiveReportKey(visibleSections[0].key)
    }
  }, [visibleSections, activeReportKey, setActiveReportKey])

  function selectCategory(nextCategory) {
    setCategory(nextCategory)

    const nextVisible = sections.filter((section) => {
      if (favouritesOnly && !favourites.includes(section.key)) return false
      if (nextCategory !== 'all' && section.category !== nextCategory) return false
      if (search.trim()) {
        const q = search.trim().toLowerCase()
        const haystack = `${section.name} ${section.key} ${section.category}`.toLowerCase()
        if (!haystack.includes(q)) return false
      }
      return true
    })

    if (nextVisible.length > 0) {
      setActiveReportKey(nextVisible[0].key)
    }
  }

  async function handleScheduleStub() {
    const active =
      visibleSections.find((section) => section.key === activeReportKey) ?? visibleSections[0]
    if (!active) return

    setScheduling(true)
    setScheduleStatus('')
    try {
      const result = await scheduleReportEmail({
        report_key: active.key,
        frequency: 'daily',
        emails: ['manager@branch.local'],
      })
      setScheduleStatus(result.message ?? 'Schedule queued (email stub).')
    } catch (err) {
      setScheduleStatus(apiErrorMessage(err, 'Unable to queue schedule.'))
    } finally {
      setScheduling(false)
    }
  }

  return (
    <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
      <div className="rounded-2xl border border-border/60 bg-card/95 p-4 shadow-luxury-sm backdrop-blur-md">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[180px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reports…"
              className="h-9 w-full rounded-xl border border-border/80 bg-background pl-9 pr-3 text-sm"
              aria-label="Search reports"
            />
          </div>
          <Button
            type="button"
            variant={favouritesOnly ? 'default' : 'outline'}
            size="sm"
            className="h-9 rounded-xl"
            onClick={() => setFavouritesOnly(!favouritesOnly)}
          >
            <Star className={cn('h-3.5 w-3.5', favouritesOnly && 'fill-current')} />
            Favourites{favourites.length > 0 ? ` (${favourites.length})` : ''}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 rounded-xl"
            disabled={scheduling}
            onClick={handleScheduleStub}
          >
            <Mail className="h-3.5 w-3.5" />
            Schedule
          </Button>
        </div>

        <div className="mt-3 flex flex-wrap gap-1 border-b border-border/50 pb-3">
          <button
            type="button"
            onClick={() => selectCategory('all')}
            className={cn(
              'rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors',
              category === 'all'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground',
            )}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => selectCategory(cat)}
              className={cn(
                'rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors',
                category === cat
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground',
              )}
            >
              {REPORT_CATEGORY_LABELS[cat] ?? cat}
            </button>
          ))}
        </div>

        <nav className="mt-3 max-h-[min(52vh,28rem)] space-y-1 overflow-y-auto pr-1" aria-label="Reports">
          {visibleSections.map((section) => {
            const isActive = section.key === activeReportKey

            return (
              <button
                key={section.key}
                type="button"
                onClick={() => setActiveReportKey(section.key)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm transition-colors',
                  isActive
                    ? 'bg-primary/10 font-semibold text-primary ring-1 ring-primary/20'
                    : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                )}
              >
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={isFavourite(section.key) ? 'Remove favourite' : 'Add favourite'}
                  className={cn('shrink-0 text-amber-500', !isFavourite(section.key) && 'opacity-40')}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleFavourite(section.key)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.stopPropagation()
                      toggleFavourite(section.key)
                    }
                  }}
                >
                  <Star className={cn('h-3.5 w-3.5', isFavourite(section.key) && 'fill-current')} />
                </span>
                <span className="min-w-0 flex-1 leading-snug">{section.name}</span>
              </button>
            )
          })}
          {visibleSections.length === 0 ? (
            <p className="px-3 py-4 text-xs text-muted-foreground">No reports match your filters.</p>
          ) : null}
        </nav>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card/95 p-4 shadow-luxury-sm backdrop-blur-md">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Shared filters
        </p>
        <ReportFilterBar
          period={period}
          compare={compare}
          customFrom={customFrom}
          customTo={customTo}
          onPeriodChange={setPeriod}
          onCompareChange={setCompare}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
        />
        {scheduleStatus ? (
          <p className="mt-2 text-xs text-muted-foreground" role="status">
            {scheduleStatus}
          </p>
        ) : null}
      </div>
    </div>
  )
}
